import Papa from 'papaparse';
import { formatDateDisplay, formatDateTime } from '@/lib/format';
import { i18n } from '@/lib/i18n';
import { t } from '@lingui/macro';
import type { Child, Contact } from '@/types';
import type { TransformedContract } from '@/types/billing';
import type { ActivityResponse } from '@/types/activities';
import { getActivityTypeLabels } from '@/types/activities';
import type { EmployeeAbsence } from '@/types/employeeAbsences';
import type { Course, CoursePlan, Attendance } from '@/types/course';
import { formatPhoneWithCountryCode } from '@/utils/referenceData';

export interface CSVExportColumn<T> {
  key: keyof T;
  header: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatter?: (value: any, row?: any) => string;
}

// CSV Security: Prevent formula injection attacks
const CSV_FORMULA_PATTERN = /^\s*[=+\-@]/;

/**
 * Sanitizes a string value for CSV export to prevent formula injection.
 * Prefixes potentially dangerous values with a single quote.
 */
function sanitizeForCSV(value: string): string {
  if (CSV_FORMULA_PATTERN.test(value)) {
    return `'${value}`;
  }
  return value;
}

export function exportToCSV<T>(
  data: T[],
  columns: CSVExportColumn<T>[],
  filename: string
) {
  // Transform data based on column configuration
  const transformedData = data.map(row => {
    const csvRow: Record<string, string> = {};
    
    columns.forEach(column => {
      const value = row[column.key];
      
      if (column.formatter) {
        // Sanitize formatter output to prevent formula injection
        const formatted = String(column.formatter(value, row) ?? '');
        csvRow[column.header] = sanitizeForCSV(formatted);
      } else if (value instanceof Date) {
        // Dates read dd.MM.yyyy in every UI language, like the screens.
        csvRow[column.header] = formatDateDisplay(value);
      } else if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
        // ISO instants export as their day; an unreadable one keeps its text, sanitised.
        csvRow[column.header] = formatDateDisplay(value) || sanitizeForCSV(value);
      } else {
        // Sanitize string values, but not numbers or other types
        const stringValue = String(value ?? '');
        csvRow[column.header] = typeof value === 'string' ? sanitizeForCSV(stringValue) : stringValue;
      }
    });
    
    return csvRow;
  });

  // Convert to CSV
  const csv = Papa.unparse(transformedData, {
    header: true,
    delimiter: ',',
  });

  // Download file
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

// Employee-specific CSV export configuration
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const employeeCSVColumns: CSVExportColumn<any>[] = [
  { key: 'firstname', header: 'First Name' },
  { key: 'lastname', header: 'Last Name' },
  { key: 'email', header: 'Email' },
  {
    key: 'phone',
    header: 'Phone',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formatter: (value: any, row?: any) => {
      if (!value) return '';
      return formatPhoneWithCountryCode(value, row?.phoneCountryCode) ?? String(value);
    },
  },
  { key: 'emailBusiness', header: 'Business Email' },
  {
    key: 'phoneBusiness',
    header: 'Business Phone',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formatter: (value: any, row?: any) => {
      if (!value) return '';
      return formatPhoneWithCountryCode(value, row?.phoneBusinessCountryCode) ?? String(value);
    },
  },
  {
    key: 'birthdate',
    header: 'Birth Date',
    formatter: (value) => {
      if (!value) return '';
      return formatDateDisplay(value) || String(value);
    }
  },
  { 
    key: 'gender', 
    header: 'Gender',
    formatter: (value) => {
      const genderMap: Record<string, string> = {
        'MALE': 'Male',
        'FEMALE': 'Female',
        'OTHER': 'Other',
        'UNKNOWN': 'Unknown'
      };
      return genderMap[value] || value;
    }
  },
  { 
    key: 'status', 
    header: 'Status',
    formatter: (value) => {
      const statusMap: Record<string, string> = {
        'ACTIVE': 'Active',
        'INACTIVE': 'Inactive',
        'UPCOMING': 'Upcoming'
      };
      return statusMap[value] || value;
    }
  },
  {
    key: 'qualificationLevel',
    header: 'Qualification Level',
    formatter: (value) => {
      const qualificationMap: Record<string, string> = {
        'QUALIFIED': 'Qualified',
        'RECOGNITION_PENDING': 'Recognition Pending',
        'NOT_QUALIFIED': 'Not Qualified'
      };
      return qualificationMap[value] || value;
    }
  },
  {
    key: 'role',
    header: 'Role',
    formatter: (value) => {
      const roleMap: Record<string, string> = {
        'INTERN': 'Intern',
        'PEDAGOGIC_ASSISTENT': 'Pedagogic Assistant',
        'APPRENTICE': 'Apprentice',
        'APPRENTICE_SHORT': 'Apprentice (Short)',
        'EDUCATOR': 'Educator',
        'GROUP_HEAD': 'Group Head',
        'MAIN_GROUP_HEAD': 'Main Group Head',
        'DAYCARE_CENTER_HEAD': 'Daycare Center Head',
        'CLEANING_STAFF': 'Cleaning Staff',
        'JANITOR': 'Janitor',
        'CHEF': 'Chef',
        'GOVERNESS': 'Governess',
        'MANAGING_DIRECTOR': 'Managing Director',
        'CONSULTANT': 'Consultant',
        'OTHER': 'Other'
      };
      return roleMap[value] || value;
    }
  },
  {
    key: 'hierarchyLevel',
    header: 'Hierarchy Level',
  },
  {
    key: 'createdAt', 
    header: 'Created',
    formatter: (value) => {
      if (!value) return '';
      return formatDateTime(value) || String(value);
    }
  },
  { 
    key: 'updatedAt', 
    header: 'Updated',
    formatter: (value) => {
      if (!value) return '';
      return formatDateTime(value) || String(value);
    }
  },
];

// Children-specific CSV export configuration
export const childrenCSVColumns: CSVExportColumn<Child>[] = [
  // Basic Information
  { key: 'childInternalId', header: 'Internal ID' },
  { key: 'firstname', header: 'First Name' },
  { key: 'lastname', header: 'Last Name' },
  { 
    key: 'birthdate', 
    header: 'Birth Date',
    formatter: (value) => {
      if (!value) return '';
      return formatDateDisplay(value) || String(value);
    }
  },
  { 
    key: 'gender', 
    header: 'Gender',
    formatter: (value) => {
      const genderMap: Record<string, string> = {
        'MALE': 'Male',
        'FEMALE': 'Female',
        'OTHER': 'Other',
        'UNKNOWN': 'Unknown'
      };
      return genderMap[value] || value;
    }
  },
  { 
    key: 'status', 
    header: 'Status',
    formatter: (value) => {
      const statusMap: Record<string, string> = {
        'ACTIVE': 'Active',
        'INACTIVE': 'Inactive',
        'UPCOMING': 'Upcoming'
      };
      return statusMap[value] || value;
    }
  },
  {
    key: 'contractSigned',
    header: 'Contract Signed',
    formatter: (value) => value === true ? 'Yes' : value === false ? 'No' : ''
  },
  
  // Medical Information
  { key: 'healthInsurance', header: 'Health Insurance' },
  { key: 'paediatrician', header: 'Pediatrician' },
  { 
    key: 'vaccinated', 
    header: 'Vaccinated',
    formatter: (value) => value === true ? 'Yes' : value === false ? 'No' : ''
  },
  { 
    key: 'useDiapers', 
    header: 'Uses Diapers',
    formatter: (value) => value === true ? 'Yes' : value === false ? 'No' : ''
  },
  { 
    key: 'allergies', 
    header: 'Allergies',
    formatter: (value) => {
      if (!value || !Array.isArray(value)) return '';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return value.map((allergy: any) => allergy.name || allergy.allergy || '').filter(Boolean).join('; ');
    }
  },
  { 
    key: 'diseases', 
    header: 'Diseases',
    formatter: (value) => {
      if (!value || !Array.isArray(value)) return '';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return value.map((disease: any) => disease.name || disease.disease || '').filter(Boolean).join('; ');
    }
  },
  { 
    key: 'medications', 
    header: 'Medications',
    formatter: (value) => {
      if (!value || !Array.isArray(value)) return '';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return value.map((med: any) => {
        const name = med.name || med.medication || '';
        const dosage = med.dosage || '';
        return dosage ? `${name} (${dosage})` : name;
      }).filter(Boolean).join('; ');
    }
  },
  { key: 'specialCare', header: 'Special Care' },
  
  // Language Information
  { 
    key: 'languagesAtHome', 
    header: 'Languages at Home',
    formatter: (value) => {
      if (!value || !Array.isArray(value)) return '';
      return value.join(', ');
    }
  },
  { 
    key: 'languagesAtDaycare', 
    header: 'Languages at Daycare',
    formatter: (value) => {
      if (!value || !Array.isArray(value)) return '';
      return value.join(', ');
    }
  },
  { key: 'languageRequest', header: 'Language Request' },
  
  // Nationality
  { 
    key: 'nationalities', 
    header: 'Nationalities',
    formatter: (value) => {
      if (!value || !Array.isArray(value)) return '';
      return value.join(', ');
    }
  },
  
  // Care Preferences
  { key: 'eatingRequest', header: 'Eating Request' },
  { key: 'sleepRequest', header: 'Sleep Request' },
  { key: 'toiletRequest', header: 'Toilet Request' },
  { key: 'miscRequest', header: 'Miscellaneous Request' },
  
  // Diet Information
  { 
    key: 'selectedDiets', 
    header: 'Selected Diets',
    formatter: (value) => {
      if (!value || !Array.isArray(value)) return '';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return value.map((diet: any) => diet.dietDefinition?.name || diet.name || '').filter(Boolean).join('; ');
    }
  },
  
  // Sleep Habits
  { 
    key: 'selectedSleepHabits', 
    header: 'Sleep Habits',
    formatter: (value) => {
      if (!value || !Array.isArray(value)) return '';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return value.map((habit: any) => habit.sleepHabitDefinition?.name || habit.name || '').filter(Boolean).join('; ');
    }
  },
  
  // Toilet Habits
  { 
    key: 'selectedToiletHabits', 
    header: 'Toilet Habits',
    formatter: (value) => {
      if (!value || !Array.isArray(value)) return '';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return value.map((habit: any) => habit.toiletHabitDefinition?.name || habit.name || '').filter(Boolean).join('; ');
    }
  },
  
  // Labels
  { 
    key: 'labels', 
    header: 'Labels',
    formatter: (value) => {
      if (!value || !Array.isArray(value)) return '';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return value.map((label: any) => label.label || label.name || '').filter(Boolean).join('; ');
    }
  },
  
  // Notes
  { key: 'notes', header: 'Notes' },
  { key: 'noteEmergencyList', header: 'Emergency List Notes' },
  
  // Timestamps
  { 
    key: 'createdAt', 
    header: 'Created',
    formatter: (value) => {
      if (!value) return '';
      return formatDateTime(value) || String(value);
    }
  },
  { 
    key: 'updatedAt', 
    header: 'Updated',
    formatter: (value) => {
      if (!value) return '';
      return formatDateTime(value) || String(value);
    }
  },
];

// Contact-specific CSV export configuration
// Use a function to ensure i18n is only accessed when needed
export const getContactCSVColumns = (): CSVExportColumn<Contact>[] => [
  // Basic Information
  { key: 'contactInternalId', header: 'Internal ID' },
  { key: 'firstname', header: 'First Name' },
  { key: 'lastname', header: 'Last Name' },
  { key: 'emailMain', header: 'Email Main' },
  { key: 'emailSecond', header: 'Email Secondary' },
  {
    key: 'telMobile',
    header: 'Mobile Phone',
    formatter: (value, row) => {
      if (!value) return '';
      return formatPhoneWithCountryCode(value, row?.telMobileCountryCode) ?? String(value);
    },
  },
  {
    key: 'telHome',
    header: 'Home Phone',
    formatter: (value, row) => {
      if (!value) return '';
      return formatPhoneWithCountryCode(value, row?.telHomeCountryCode) ?? String(value);
    },
  },
  {
    key: 'telWork',
    header: 'Work Phone',
    formatter: (value, row) => {
      if (!value) return '';
      return formatPhoneWithCountryCode(value, row?.telWorkCountryCode) ?? String(value);
    },
  },
  { 
    key: 'birthdate', 
    header: 'Birth Date',
    formatter: (value) => {
      if (!value) return '';
      return formatDateDisplay(value) || String(value);
    }
  },
  { 
    key: 'gender', 
    header: 'Gender',
    formatter: (value) => {
      const genderMap: Record<string, string> = {
        'MALE': 'Male',
        'FEMALE': 'Female',
        'OTHER': 'Other',
        'UNKNOWN': 'Unknown'
      };
      return genderMap[value] || value;
    }
  },
  { 
    key: 'status', 
    header: 'Status',
    formatter: (value) => {
      const statusMap: Record<string, string> = {
        'ACTIVE': 'Active',
        'INACTIVE': 'Inactive',
        'UPCOMING': 'Upcoming'
      };
      return statusMap[value] || value;
    }
  },
  
  // #1156: twelve columns were dropped here — familyName, familyStatus,
  // relationToChildren, street, houseNumber, zip, city, country,
  // isEmergencyContact, isPickupAllowed, priorityOnEmergencyList, isCustomer.
  // None of them exists on `models_Contact`, so every one of them exported a
  // blank cell. They cannot be re-sourced from the rows this table holds: the
  // address parts live on the nested optional `address?: models_Address`, which
  // the contacts LIST endpoint never populates (`mapContactFromDB` sets
  // `AddressID` only — `internal/repositories/contact_repository_impl.go`), the
  // relationship flags are per child-contact-relationship rather than per
  // contact, and `familyName`/`familyStatus`/`isCustomer` exist on no shape at
  // all. Re-sourcing would need a backend change (there is an unused
  // server-side `GET /api/v1/contacts/export`), which is out of scope here.
  // See docs/notes/pr-1156-batch4-report.md for the per-column decision table.

  // Customer Information
  { key: 'customerScore', header: t`contacts.fields.customerScore` }, // Default header, will be overridden in ContactsTable
  
  // Labels
  { 
    key: 'labels', 
    header: 'Labels',
    formatter: (value) => {
      if (!value || !Array.isArray(value)) return '';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return value.map((label: any) => label.label || label.name || '').filter(Boolean).join('; ');
    }
  },
  
  // Notes
  { key: 'notes', header: 'Notes' },
  
  // Timestamps
  { 
    key: 'createdAt', 
    header: 'Created',
    formatter: (value) => {
      if (!value) return '';
      return formatDateTime(value) || String(value);
    }
  },
  { 
    key: 'updatedAt', 
    header: 'Updated',
    formatter: (value) => {
      if (!value) return '';
      return formatDateTime(value) || String(value);
    }
  },
];

// Contract-specific CSV export configuration.
// Typed on `TransformedContract` (camelCase), not the snake_case wire
// `billing_ContractResponse`: `ContractsTable.handleExportCSV` passes the rows the
// table already holds, which are transformed. The retype is type-only for every
// column except `Status`, whose dead `effective_status` key was repaired to
// `effectiveStatus` — see the in-place comment on that column and report §3.20
// (docs/notes/pr-1156-batch4-report.md).
export const contractsCSVColumns: CSVExportColumn<TransformedContract>[] = [
  // Basic Information
  { key: 'id', header: 'Contract ID' },
  { key: 'childInternalId', header: 'Child Internal ID' },
  { key: 'childName', header: 'Child Name' },
  {
    key: 'startDate',
    header: 'Start Date',
    formatter: (value) => {
      if (!value) return '';
      return formatDateDisplay(value) || String(value);
    }
  },
  {
    key: 'endDate',
    header: 'End Date',
    formatter: (value) => {
      if (!value) return '';
      return formatDateDisplay(value) || String(value);
    }
  },
  {
    key: 'subsidised',
    header: 'Subsidised',
    formatter: (value) => value === true ? 'Yes' : value === false ? 'No' : ''
  },
  {
    key: 'tariffVersions',
    header: 'Tariff Versions',
    formatter: (value) => {
      if (!value || !Array.isArray(value)) return '';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return value.map((tv: any) => tv.tariffVersionName || tv.groupType || '').filter(Boolean).join('; ');
    }
  },
  {
    // Dead-read repair (#1156). The rows this list formats are camelCased by
    // `ContractTransformer.toFrontend` (wire `effective_status` -> `effectiveStatus`),
    // so the old `effective_status` key resolved to `undefined` on every row and the
    // Status column silently fell back to the raw lifecycle `status`. A contract with
    // `status: active` but `effective_status: upcoming` exported "Active" instead of
    // "Upcoming". The snake_case key is kept as a secondary fallback so an
    // untransformed row still exports the same value it did before.
    key: 'effectiveStatus',
    header: 'Status',
    formatter: (value, row) => {
      const raw = (
        value ||
        (row as Record<string, unknown>)?.effective_status ||
        (row as Record<string, unknown>)?.status ||
        ''
      ).toString().toUpperCase();
      const normalized = (() => {
        if (raw === 'ACTIVE') return 'IN_EFFECT';
        if (raw === 'ARCHIVED') return 'EXPIRED';
        if (raw === 'DRAFT' || raw === 'FUTURE') return 'UPCOMING';
        return raw;
      })();

      const statusMap: Record<string, string> = {
        IN_EFFECT: i18n._(t({ id: 'common.active', message: 'Active' })),
        UPCOMING: i18n._(t({ id: 'common.upcoming', message: 'Upcoming' })),
        EXPIRED: i18n._(t({ id: 'common.expired', message: 'Expired' })),
        CANCELLED: i18n._(t({ id: 'common.cancelled', message: 'Cancelled' })),
        TERMINATED: i18n._(t({ id: 'common.terminated', message: 'Terminated' })),
      };
      return statusMap[normalized] || normalized;
    }
  },

  // Financial Information
  {
    key: 'estimatedMonthlyAmount',
    header: 'Monthly Amount',
    formatter: (value) => {
      if (value === undefined || value === null) return '';
      const amount = Number(value) / 100; // Convert Rappen to CHF
      return amount.toFixed(2);
    }
  },

  // Payers Information
  {
    key: 'payers',
    header: 'Payers',
    formatter: (value) => {
      if (!value || !Array.isArray(value)) return '';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return value.map((payer: any) => `${payer.contactName} (${payer.paymentPercentage}%)`).filter(Boolean).join('; ');
    }
  },

  // Service Pattern
  {
    key: 'servicePattern',
    header: 'Service Pattern',
    formatter: (value) => {
      if (!value || typeof value !== 'object') return '';
      const days = Object.keys(value).filter(day => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const services = (value as any)[day];
        return services && Array.isArray(services) && services.length > 0;
      });
      return days.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(', ');
    }
  },

  // Notes
  { key: 'notes', header: 'Notes' },

  // Timestamps
  {
    key: 'createdAt',
    header: 'Created',
    formatter: (value) => {
      if (!value) return '';
      return formatDateTime(value) || String(value);
    }
  },
  {
    key: 'updatedAt',
    header: 'Updated',
    formatter: (value) => {
      if (!value) return '';
      return formatDateTime(value) || String(value);
    }
  },
];

// Childcare-specific CSV export configuration
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const childcareCSVColumns: CSVExportColumn<any>[] = [
  { key: 'name', header: 'Name' },
  {
    key: 'address_id',
    header: 'Address',
    formatter: () => {
      // For now, we just show that it has an address
      // In a real implementation, you might want to fetch the address details
      return 'Has Address';
    }
  },
  {
    key: 'created_at',
    header: 'Created',
    formatter: (value) => {
      if (!value) return '';
      return formatDateTime(value) || String(value);
    }
  },
];

// Activity-specific CSV export configuration
// Use a function to ensure i18n is only accessed when needed
// The _locale parameter forces React useMemo to recalculate when locale changes
export const getActivityCSVColumns = (_locale?: string): CSVExportColumn<ActivityResponse>[] => [
  {
    key: 'child' as keyof ActivityResponse,
    header: i18n._(t({ id: 'table.activities.columns.childName', message: 'Child Name' })),
    formatter: (value) => {
      if (!value) return '';
      const child = value as ActivityResponse['child'];
      return `${child?.firstname || ''} ${child?.lastname || ''}`.trim();
    }
  },
  {
    key: 'child' as keyof ActivityResponse,
    header: i18n._(t({ id: 'table.activities.columns.childInternalId', message: 'Child Internal ID' })),
    formatter: (value) => {
      if (!value) return '';
      const child = value as ActivityResponse['child'];
      return child?.internal_id || '';
    }
  },
  {
    key: 'type' as keyof ActivityResponse,
    header: i18n._(t({ id: 'table.activities.columns.type', message: 'Type' })),
    formatter: (value) => {
      if (!value) return '';
      const labels = getActivityTypeLabels(i18n);
      return labels[value as string] || String(value);
    }
  },
  {
    key: 'activity_time' as keyof ActivityResponse,
    header: i18n._(t({ id: 'table.activities.columns.activityTime', message: 'Activity Time' })),
    formatter: (value) => {
      if (!value) return '';
      return formatDateTime(value as string) || String(value);
    }
  },
  {
    key: 'recorded_by' as keyof ActivityResponse,
    header: i18n._(t({ id: 'table.activities.columns.recordedBy', message: 'Recorded By' })),
    formatter: (value) => {
      if (!value) return '';
      const user = value as ActivityResponse['recorded_by'];
      return `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
    }
  },
  {
    key: 'created_at' as keyof ActivityResponse,
    header: i18n._(t({ id: 'table.activities.columns.createdAt', message: 'Created At' })),
    formatter: (value) => {
      if (!value) return '';
      return formatDateTime(value as string) || String(value);
    }
  },
  {
    key: 'comments' as keyof ActivityResponse,
    header: i18n._(t({ id: 'table.activities.columns.comments', message: 'Comments' })),
    formatter: (value) => {
      if (!value || !Array.isArray(value)) return '';
      return value.map((c: { text: string }) => c.text).join(' | ');
    }
  },
  {
    key: 'data' as keyof ActivityResponse,
    header: i18n._(t({ id: 'table.activities.columns.details', message: 'Details' })),
    formatter: (value) => {
      if (!value || typeof value !== 'object') return '';
      return JSON.stringify(value);
    }
  },
];

// Employee Absence-specific CSV export configuration
// Use a function to ensure i18n is only accessed when needed
export const getAbsenceCSVColumns = (): CSVExportColumn<EmployeeAbsence>[] => [
  {
    key: 'employee' as keyof EmployeeAbsence,
    header: i18n._(t({ id: 'absences.table.employee', message: 'Employee' })),
    formatter: (value) => {
      if (!value) return '';
      const employee = value as EmployeeAbsence['employee'];
      return `${employee?.firstname || ''} ${employee?.lastname || ''}`.trim();
    }
  },
  {
    key: 'absence_type' as keyof EmployeeAbsence,
    header: i18n._(t({ id: 'absences.table.type', message: 'Type' })),
    formatter: (value) => {
      if (!value) return '';
      // Use the same translation keys as AbsenceTypeBadge
      const typeMap: Record<string, string> = {
        'VACATION': i18n._(t({ id: 'absences.type.vacation', message: 'Vacation' })),
        'SICK': i18n._(t({ id: 'absences.type.sick', message: 'Sick' })),
        'PERSONAL': i18n._(t({ id: 'absences.type.personal', message: 'Personal' })),
        'TRAINING': i18n._(t({ id: 'absences.type.training', message: 'Training' })),
        'OTHER': i18n._(t({ id: 'absences.type.other', message: 'Other' })),
        'ACCIDENT': i18n._(t({ id: 'absences.type.accident', message: 'Accident' })),
        'COMP_TIME': i18n._(t({ id: 'absences.type.compTime', message: 'Comp time' })),
        'CHILD_SICK': i18n._(t({ id: 'absences.type.childSick', message: 'Child sick' })),
      };
      return typeMap[value as string] || String(value);
    }
  },
  {
    key: 'from_date' as keyof EmployeeAbsence,
    header: i18n._(t({ id: 'absences.table.fromDate', message: 'From' })),
    formatter: (value) => {
      if (!value) return '';
      return formatDateDisplay(value as string) || String(value);
    }
  },
  {
    key: 'to_date' as keyof EmployeeAbsence,
    header: i18n._(t({ id: 'absences.table.toDate', message: 'To' })),
    formatter: (value) => {
      if (!value) return '';
      return formatDateDisplay(value as string) || String(value);
    }
  },
  {
    key: 'full_day' as keyof EmployeeAbsence,
    header: i18n._(t({ id: 'absences.table.fullDay', message: 'Full Day' })),
    formatter: (value) => {
      return value === true
        ? i18n._(t({ id: 'common.yes', message: 'Yes' }))
        : i18n._(t({ id: 'common.no', message: 'No' }));
    }
  },
  {
    key: 'notes' as keyof EmployeeAbsence,
    header: i18n._(t({ id: 'absences.table.notes', message: 'Notes' })),
    formatter: (value) => {
      return value ? String(value) : '';
    }
  },
];

// Course-specific CSV export configuration
export const getCourseCSVColumns = (currency: string = 'CHF'): CSVExportColumn<Course>[] => [
  {
    key: 'name' as keyof Course,
    header: i18n._(t({ id: 'courses.table.name', message: 'Name' })),
  },
  {
    key: 'description' as keyof Course,
    header: i18n._(t({ id: 'courses.table.description', message: 'Description' })),
  },
  {
    key: 'instructor_firstname' as keyof Course,
    header: i18n._(t({ id: 'courses.table.instructorFirstName', message: 'Instructor First Name' })),
  },
  {
    key: 'instructor_lastname' as keyof Course,
    header: i18n._(t({ id: 'courses.table.instructorLastName', message: 'Instructor Last Name' })),
  },
  {
    key: 'price_per_unit' as keyof Course,
    header: i18n._(t({ id: 'courses.table.pricePerSession', message: 'Price per Session' })),
    formatter: (value) => {
      if (!value) return '';
      const cents = parseInt(String(value), 10);
      if (isNaN(cents)) return '';
      return `${currency} ${(cents / 100).toFixed(2)}`;
    }
  },
  {
    key: 'price_per_month' as keyof Course,
    header: i18n._(t({ id: 'courses.table.pricePerMonth', message: 'Price per Month' })),
    formatter: (value) => {
      if (!value) return '';
      const cents = parseInt(String(value), 10);
      if (isNaN(cents)) return '';
      return `${currency} ${(cents / 100).toFixed(2)}`;
    }
  },
  {
    key: 'plan_count' as keyof Course,
    header: i18n._(t({ id: 'courses.table.planCount', message: 'Plan Count' })),
    formatter: (value) => String(value ?? 0),
  },
  {
    key: 'archived_at' as keyof Course,
    header: i18n._(t({ id: 'courses.table.status', message: 'Status' })),
    formatter: (value) => {
      return value
        ? i18n._(t({ id: 'common.archived', message: 'Archived' }))
        : i18n._(t({ id: 'common.active', message: 'Active' }));
    }
  },
  {
    key: 'created_at' as keyof Course,
    header: i18n._(t({ id: 'common.createdAt', message: 'Created At' })),
    formatter: (value) => {
      if (!value) return '';
      return formatDateTime(value as string) || String(value);
    }
  },
];

// Course Plan-specific CSV export configuration
const DAYS_OF_WEEK_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const getCoursePlanCSVColumns = (): CSVExportColumn<CoursePlan>[] => [
  {
    key: 'course_name' as keyof CoursePlan,
    header: i18n._(t({ id: 'coursePlans.table.course', message: 'Course' })),
  },
  {
    key: 'group_name' as keyof CoursePlan,
    header: i18n._(t({ id: 'coursePlans.table.group', message: 'Group' })),
    formatter: (value) => value || i18n._(t({ id: 'coursePlans.allGroups', message: 'All Groups' })),
  },
  {
    key: 'day_of_week' as keyof CoursePlan,
    header: i18n._(t({ id: 'coursePlans.table.dayOfWeek', message: 'Day of Week' })),
    formatter: (value) => {
      if (value === undefined || value === null) return '';
      return DAYS_OF_WEEK_LABELS[value as number] || String(value);
    }
  },
  {
    key: 'start_time' as keyof CoursePlan,
    header: i18n._(t({ id: 'coursePlans.table.startTime', message: 'Start Time' })),
  },
  {
    key: 'end_time' as keyof CoursePlan,
    header: i18n._(t({ id: 'coursePlans.table.endTime', message: 'End Time' })),
  },
  {
    key: 'recurrence_rule' as keyof CoursePlan,
    header: i18n._(t({ id: 'coursePlans.table.recurrence', message: 'Recurrence' })),
    formatter: (value) => {
      const recurrenceMap: Record<string, string> = {
        'weekly': i18n._(t({ id: 'coursePlans.recurrence.weekly', message: 'Weekly' })),
        'biweekly': i18n._(t({ id: 'coursePlans.recurrence.biweekly', message: 'Bi-weekly' })),
        'monthly': i18n._(t({ id: 'coursePlans.recurrence.monthly', message: 'Monthly' })),
        'once': i18n._(t({ id: 'coursePlans.recurrence.once', message: 'One-time' })),
      };
      return recurrenceMap[value as string] || String(value || '');
    }
  },
  {
    key: 'effective_from' as keyof CoursePlan,
    header: i18n._(t({ id: 'coursePlans.table.effectiveFrom', message: 'Effective From' })),
    formatter: (value) => {
      if (!value) return '';
      return formatDateDisplay(value as string) || String(value);
    }
  },
  {
    key: 'effective_until' as keyof CoursePlan,
    header: i18n._(t({ id: 'coursePlans.table.effectiveUntil', message: 'Effective Until' })),
    formatter: (value) => {
      if (!value) return '';
      return formatDateDisplay(value as string) || String(value);
    }
  },
  {
    key: 'enrolled_count' as keyof CoursePlan,
    header: i18n._(t({ id: 'coursePlans.table.enrolled', message: 'Enrolled' })),
    formatter: (value) => String(value ?? 0),
  },
  {
    key: 'max_capacity' as keyof CoursePlan,
    header: i18n._(t({ id: 'coursePlans.table.maxCapacity', message: 'Max Capacity' })),
    formatter: (value) => value ? String(value) : i18n._(t({ id: 'common.unlimited', message: 'Unlimited' })),
  },
  {
    key: 'archived_at' as keyof CoursePlan,
    header: i18n._(t({ id: 'coursePlans.table.status', message: 'Status' })),
    formatter: (value) => {
      return value
        ? i18n._(t({ id: 'common.archived', message: 'Archived' }))
        : i18n._(t({ id: 'common.active', message: 'Active' }));
    }
  },
  {
    key: 'created_at' as keyof CoursePlan,
    header: i18n._(t({ id: 'common.createdAt', message: 'Created At' })),
    formatter: (value) => {
      if (!value) return '';
      return formatDateTime(value as string) || String(value);
    }
  },
];

// Workforce-specific CSV export configuration
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getWorkforceCSVColumns = (): CSVExportColumn<any>[] => [
  {
    key: 'employeeName',
    header: i18n._(t({ id: 'workforce.employeeName', message: 'Employee' })),
  },
  {
    key: 'targetFormatted',
    header: i18n._(t({ id: 'workforce.targetHours', message: 'Target Hours' })),
  },
  {
    key: 'plannedFormatted',
    header: i18n._(t({ id: 'workforce.plannedHours', message: 'Planned Hours' })),
  },
  {
    key: 'actualFormatted',
    header: i18n._(t({ id: 'workforce.actualHours', message: 'Actual Hours' })),
  },
  {
    key: 'deltaMinutes',
    header: i18n._(t({ id: 'workforce.delta', message: 'Delta' })),
    formatter: (value) => {
      const mins = Number(value) || 0;
      const sign = mins >= 0 ? '+' : '-';
      const abs = Math.abs(mins);
      const h = Math.floor(abs / 60);
      const m = abs % 60;
      return `${sign}${h}h ${m}m`;
    },
  },
];

// Timesheet-specific CSV export configuration
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getTimesheetCSVColumns = (): CSVExportColumn<any>[] => [
  {
    key: 'employeeName',
    header: i18n._(t({ id: 'timeTracking.table.employee', message: 'Employee' })),
  },
  {
    key: 'dayFormatted',
    header: i18n._(t({ id: 'timeTracking.table.day', message: 'Day' })),
  },
  {
    key: 'clockIn',
    header: i18n._(t({ id: 'timeTracking.clockIn', message: 'Clock In' })),
  },
  {
    key: 'clockOut',
    header: i18n._(t({ id: 'timeTracking.clockOut', message: 'Clock Out' })),
  },
  {
    key: 'breaksCount',
    header: i18n._(t({ id: 'timeTracking.breaks', message: 'Breaks' })),
    formatter: (value) => String(value ?? 0),
  },
  {
    key: 'grossHours',
    header: i18n._(t({ id: 'timeTracking.grossHours', message: 'Gross Hours' })),
  },
  {
    key: 'breakTime',
    header: i18n._(t({ id: 'timeTracking.breakTime', message: 'Break Time' })),
  },
  {
    key: 'netHours',
    header: i18n._(t({ id: 'timeTracking.netHours', message: 'Net Hours' })),
  },
  {
    key: 'status',
    header: i18n._(t({ id: 'timeTracking.status', message: 'Status' })),
  },
  {
    key: 'source',
    header: i18n._(t({ id: 'timeTracking.source', message: 'Source' })),
    formatter: (value) => value ? String(value).toLowerCase() : '',
  },
];

// Course Attendance-specific CSV export configuration
export const getAttendanceCSVColumns = (currency: string = 'CHF'): CSVExportColumn<Attendance>[] => [
  {
    key: 'child_firstname' as keyof Attendance,
    header: i18n._(t({ id: 'attendance.table.childFirstName', message: 'Child First Name' })),
  },
  {
    key: 'child_lastname' as keyof Attendance,
    header: i18n._(t({ id: 'attendance.table.childLastName', message: 'Child Last Name' })),
  },
  {
    key: 'course_name' as keyof Attendance,
    header: i18n._(t({ id: 'attendance.table.course', message: 'Course' })),
  },
  {
    key: 'attendance_date' as keyof Attendance,
    header: i18n._(t({ id: 'attendance.table.date', message: 'Date' })),
    formatter: (value) => {
      if (!value) return '';
      return formatDateDisplay(value as string) || String(value);
    }
  },
  {
    key: 'status' as keyof Attendance,
    header: i18n._(t({ id: 'attendance.table.status', message: 'Status' })),
    formatter: (value) => {
      const statusMap: Record<string, string> = {
        'pending': i18n._(t({ id: 'attendance.status.pending', message: 'Pending' })),
        'approved': i18n._(t({ id: 'attendance.status.approved', message: 'Approved' })),
        'cancelled': i18n._(t({ id: 'attendance.status.cancelled', message: 'Cancelled' })),
      };
      return statusMap[value as string] || String(value || '');
    }
  },
  {
    key: 'amount' as keyof Attendance,
    header: i18n._(t({ id: 'attendance.table.amount', message: 'Amount' })),
    formatter: (value) => {
      if (!value) return '';
      const cents = parseInt(String(value), 10);
      if (isNaN(cents)) return '';
      return `${currency} ${(cents / 100).toFixed(2)}`;
    }
  },
  {
    key: 'invoice_id' as keyof Attendance,
    header: i18n._(t({ id: 'attendance.table.billed', message: 'Billed' })),
    formatter: (value) => {
      return value
        ? i18n._(t({ id: 'common.yes', message: 'Yes' }))
        : i18n._(t({ id: 'common.no', message: 'No' }));
    }
  },
  {
    key: 'billed_at' as keyof Attendance,
    header: i18n._(t({ id: 'attendance.table.billedAt', message: 'Billed At' })),
    formatter: (value) => {
      if (!value) return '';
      return formatDateTime(value as string) || String(value);
    }
  },
  {
    key: 'created_at' as keyof Attendance,
    header: i18n._(t({ id: 'common.createdAt', message: 'Created At' })),
    formatter: (value) => {
      if (!value) return '';
      return formatDateTime(value as string) || String(value);
    }
  },
]; 
